import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Grid, Paper, Typography, Tab, Tabs, Box, LinearProgress, Container, Button } from '@mui/material';
import { useRecoilValue } from 'recoil';

import AddressContainer from '../Address';
import CustomerProfileContainer from '../CustomerProfile';
import AddressLocator from '../AddressLocator';
import DeliverySearchContainer from '../DeliverySearch';
import DeliveryHistory from '../DeliveryHistory';
import { H2, H4 } from '../../components/Heading';
import { selectedProfileState, useSelectProfileCallback } from '../../states/profile';
import { BlueBox } from '../../components/Box';

const styles = {
  error: { border: 'solid 1px #f4485e', borderLeft: 'solid 8px #f4485e', padding: '10px', marginBottom: '6px', borderRadius: '5px' },
  success: { border: 'solid 2px #75c31e', borderLeft: 'solid 8px #75c31e', padding: '10px', marginBottom: '6px', borderRadius: '5px' },
  failed: { border: 'solid 2px #4a4a4a', borderLeft: 'solid 8px #4a4a4a', padding: '10px', marginBottom: '6px', borderRadius: '5px' },
  normal: { padding: '15px', width: '100%' },
  item: {
    fontSize: '15px',
    padding: '2px',
    color: '#5a5a5a',
  },
};

function CustomerProfileInfo() {
  const profile = useRecoilValue(selectedProfileState);
  const { customer } = profile || {};
  return (
    <>
      <Container style={{ display: 'flex', padding: 0 }}>
        <div style={{ flex: 1 }}>
          <H2>Customer Profile Info</H2>
        </div>
      </Container>
      {customer && (
        <BlueBox>
          <Grid container>
            <Grid size={7}>
              <H4>Customer</H4>
              <Typography>{customer.name}</Typography>
            </Grid>
            <Grid size={5}>
              <H4>Phone</H4>
              <Typography>{customer.phone_number}</Typography>
            </Grid>
          </Grid>
        </BlueBox>
      )}

      <div style={{ marginBottom: '10px' }}>
        <React.Suspense fallback={<LinearProgress />}>
          <CustomerProfileContainer />
        </React.Suspense>
      </div>
      <React.Suspense fallback={<LinearProgress />}>
        <AddressContainer />
      </React.Suspense>
    </>
  );
}

function CustomerProfileScreen() {
  const params = useParams();
  const [searchTab, setSearchTab] = useState(0);
  const selectProfile = useSelectProfileCallback();
  let navigate = useNavigate();
  const handleChange = (event, newValue) => {
    setSearchTab(newValue);
  };

  useEffect(() => {
    selectProfile(params.id);
  }, [selectProfile, params.id]);

  const goBack = () => {
    navigate(`/incident`, { replace: true });
  };

  return (
    <Container maxWidth="xl" style={{ height: 'calc(100% - 65px)', paddingTop: '10px' }}>
      <Paper elevation={2}>
        <Container maxWidth="xl" style={{ padding: '15px', display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={goBack} disableElevation size='small' color='inherit'>
            Back
          </Button>
        </Container>
        <React.Suspense fallback={<div>Loading...</div>}>
          <Container maxWidth="xl" style={{ padding: '15px' }}>
            <Grid container spacing={2} style={{ minHeight: 'calc(100vh - 200px)' }}>
              <Grid size={4} container>
                <Paper sx={styles.success}>
                  <React.Suspense fallback={<LinearProgress />}>
                    <CustomerProfileInfo />
                  </React.Suspense>
                </Paper>
              </Grid>
              <Grid size={4} container>
                <Paper elevation={2} sx={styles.normal}>
                  <Container style={{ padding: 0, height: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1.25 }}>
                      <Tabs value={searchTab} onChange={handleChange} aria-label="Delivery History Tab">
                        <Tab label="Address History" id="tab-address-history" />
                        <Tab label="Custom Search" id="tab-custom-search" />
                      </Tabs>
                    </Box>
                    <div role="tabpanel" hidden={searchTab !== 0} id={`tabpanel-address-history`} aria-labelledby={`tab-address-history`} style={{ height: 'calc(100% - 70px)', overflow: 'auto' }}>
                      {searchTab === 0 && (
                        <React.Suspense fallback={<LinearProgress />}>
                          <DeliveryHistory />
                        </React.Suspense>
                      )}
                    </div>
                    <div
                      role="tabpanel"
                      hidden={searchTab !== 1}
                      style={{ height: 'calc(100% - 70px)', maxHeight: 'calc(100% - 70px)' }}
                    >
                      {searchTab === 1 && (
                        <React.Suspense fallback={<LinearProgress />}>
                          <DeliverySearchContainer />
                        </React.Suspense>
                      )}
                    </div>
                  </Container>
                </Paper>
              </Grid>
              <Grid size={4}>
                <React.Suspense fallback={<LinearProgress />}>
                  <AddressLocator />
                </React.Suspense>
              </Grid>
            </Grid>
          </Container>
        </React.Suspense>
      </Paper>
    </Container>
  );
}

export default CustomerProfileScreen;
